import React from "react";
import { useParams } from "react-router-dom";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { usePublicListingByHandle } from "../../hooks/use-public-listing-by-handle";
import { usePublicListingStats } from "../../hooks/use-public-listing-stats";
import CompanyApps from "./CompanyApps";
import CompanyFooter from "./CompanyFooter";
import CompanyHeader from "./CompanyHeader";
import CompanyNotFound from "./CompanyNotFound";
import "./CompanyPage.scss";
import CountryDistribution from "./CountryDistribution";
import TrendlineChart from "./TrendlineChart";

function formatCompactCurrency(cents: number, currency: string): string {
  const value = cents / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return `$${(value / 1000).toFixed(1)}k`;
  }
}

const CompanyPage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const { publicListing, isLoading: listingLoading } =
    usePublicListingByHandle(handle);
  const {
    normalized,
    withAmounts,
    countries,
    isLoading: statsLoading,
  } = usePublicListingStats(publicListing);

  useDocumentTitle(
    publicListing?.companyName
      ? `${publicListing.companyName} - VCDB`
      : "Company - VCDB",
  );

  if (listingLoading || statsLoading) {
    return <LoadingMask message="Loading..." />;
  }

  if (!publicListing) {
    return (
      <div className="company-page">
        <CompanyNotFound />
      </div>
    );
  }

  const showRevenue = publicListing.revenueDisplay !== "hidden";
  const showSubscriptions = publicListing.subscriptionsDisplay !== "hidden";
  const showCountries = publicListing.countryDisplay !== "hidden";

  // Build amount arrays when display setting is "trendline-with-amounts"
  const revenueAmounts =
    publicListing.revenueDisplay === "trendline-with-amounts" && withAmounts
      ? withAmounts.revenue.map((r) => r.revenue)
      : undefined;

  const currency = withAmounts?.currency ?? "usd";
  const formatRevenue = (cents: number): string =>
    formatCompactCurrency(cents, currency);

  const customerAmounts =
    publicListing.subscriptionsDisplay === "trendline-with-amounts" &&
    withAmounts
      ? withAmounts.revenue.map((r) => r.customers)
      : undefined;

  const formatCustomers = (count: number): string =>
    count >= 1000 ? `${(count / 1000).toFixed(1)}K` : String(Math.round(count));

  return (
    <div className="company-page">
      <div className="company-card">
        <CompanyHeader listing={publicListing} />

        {publicListing.appEntries && publicListing.appEntries.length > 0 && (
          <CompanyApps appEntries={publicListing.appEntries} />
        )}

        {showRevenue && normalized && normalized.revenue.length > 0 && (
          <TrendlineChart
            title="Monthly Revenue"
            data={normalized.revenue}
            amounts={revenueAmounts}
            formatAmount={formatRevenue}
          />
        )}

        {showSubscriptions &&
          normalized &&
          normalized.subscriptions.length > 0 && (
            <TrendlineChart
              title="Monthly Customers"
              data={normalized.subscriptions}
              amounts={customerAmounts}
              formatAmount={formatCustomers}
              color="#b8336a"
            />
          )}

        {showCountries && countries && countries.countries.length > 0 && (
          <CountryDistribution
            countries={countries.countries}
            displayLevel={publicListing.countryDisplay}
          />
        )}
        <div className="stripe-footer-badge">
          All data sourced directly from{" "}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Stripe
          </a>
        </div>

        <CompanyFooter />
      </div>
    </div>
  );
};

export default CompanyPage;
